import { html, nothing, type TemplateResult } from 'lit';
import {
  DIMENSION_LABELS,
  formatMapLink,
  type ReleaseManifest,
  type PublicEpisode,
  type PublicCharacter,
  type CanonStatus,
} from '@ka/contracts';
import { spiderSvg, type KaYouTube } from '@ka/ui';
import { assetUrl, byNumberDesc } from './data.ts';
import type { Route } from './router.ts';

export interface Page {
  title: string;
  body: TemplateResult;
}

const fmtTime = (t: number) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

const canonLabel: Record<CanonStatus, string> = {
  established: 'Established canon',
  'draft-idea': 'Idea, not yet canon',
  'open-question': 'Open question',
};

export const notFound = (): Page => ({
  title: 'Lost in the caves',
  body: html`<section class="panel">
    <h1>Lost in the caves</h1>
    <p>This tunnel leads nowhere. <a href="/">Return to the surface</a>.</p>
  </section>`,
});

function episodeCard(m: ReleaseManifest, e: PublicEpisode) {
  const thumb = assetUrl(m, e.thumbnailAssetId);
  return html`<li class="book">
    <a href="/episodes/${e.slug}">
      ${thumb ? html`<img src=${thumb} alt="" loading="lazy" width="320" height="180" />` : html`<div class="book-cover" aria-hidden="true">${spiderSvg(40)}</div>`}
      <span class="book-num">Episode ${e.number}</span>
      <span class="book-title">${e.title}</span>
    </a>
  </li>`;
}

function characterCard(c: PublicCharacter) {
  return html`<li class="panel char accent-${c.accent}">
    <h3><a href="/characters/${c.slug}">${c.displayName}</a></h3>
    <p>${c.tagline}</p>
  </li>`;
}

export function home(m: ReleaseManifest | null): Page {
  const latest = m ? [...m.episodes].sort(byNumberDesc)[0] : undefined;
  return {
    title: 'King Arachnid Chronicles',
    body: html`
      <section class="hero">
        <p class="kicker">A Minecraft adventure</p>
        <h1>King Arachnid Chronicles</h1>
        <p class="lede">
          Follow King Arachnid and his faithful companion Kynorum as they explore, build and survive
          in their own Minecraft world.
        </p>
        <div class="ctas">
          ${latest ? html`<a class="btn primary" href="/episodes/${latest.slug}">▶ Watch Latest Adventure</a>` : nothing}
          <a class="btn green" href="/map">Explore the Kingdom</a>
        </div>
      </section>
      ${
        latest
          ? html`<section>
              <h2>Latest adventure</h2>
              <ul class="books">
                ${episodeCard(m!, latest)}
              </ul>
            </section>`
          : html`<section class="panel">
              <h2>The first adventure is on its way</h2>
              <p>Episodes will appear here once they are published. Check back soon!</p>
            </section>`
      }
      ${
        m?.characters.length
          ? html`<section>
              <h2>Meet the characters</h2>
              <ul class="grid plain">
                ${m.characters.map(characterCard)}
              </ul>
            </section>`
          : nothing
      }
      <ka-spider-symbol symbol="home" class="hidden-spider"></ka-spider-symbol>
    `,
  };
}

export function episodes(m: ReleaseManifest): Page {
  const list = [...m.episodes].sort(byNumberDesc);
  return {
    title: 'Episodes',
    body: html`<h1>Episodes</h1>
      ${
        list.length
          ? html`<ul class="books">
              ${list.map((e) => episodeCard(m, e))}
            </ul>`
          : html`<p class="panel">No episodes yet — the first adventure is coming soon.</p>`
      }
      <ka-spider-symbol symbol="episodes" class="hidden-spider"></ka-spider-symbol>`,
  };
}

export function episode(m: ReleaseManifest, r: Route): Page {
  const e = m.episodes.find((x) => x.slug === r.params.slug);
  if (!e) return notFound();
  const landmarks = m.landmarks.filter((l) => e.landmarkIds.includes(l.id));
  const seek = (t: number) => (ev: Event) => {
    const root = (ev.target as Element).getRootNode() as ParentNode;
    root.querySelector<KaYouTube>('ka-youtube')?.playAt(t);
  };
  return {
    title: `Episode ${e.number}: ${e.title}`,
    body: html`
      <p class="kicker"><a href="/episodes">Episodes</a> › Episode ${e.number}</p>
      <h1>${e.title}</h1>
      <ka-youtube
        video-id=${e.youtubeId ?? ''}
        title=${e.title}
        thumbnail=${assetUrl(m, e.thumbnailAssetId) ?? ''}
      ></ka-youtube>
      <div class="two-col">
        <div>
          <ka-markdown .source=${e.summary}></ka-markdown>
          ${
            e.chapters.length
              ? html`<h2>Chapters</h2>
                  <ol class="chapters">
                    ${e.chapters.map((c) => html`<li><button class="link" @click=${seek(c.t)}>${fmtTime(c.t)}</button> ${c.label}</li>`)}
                  </ol>`
              : nothing
          }
        </div>
        <aside class="panel">
          <h2>Places in this episode</h2>
          ${
            landmarks.length
              ? html`<ul>
                  ${landmarks.map((l) => html`<li><a href="/map?${formatMapLink({ snapshot: e.map?.snapshotId, landmark: l.id })}">${l.name}</a></li>`)}
                </ul>`
              : html`<p class="muted">No landmarks marked yet.</p>`
          }
          ${
            e.map
              ? html`<p>
                  <a class="btn green" href="/map?${formatMapLink({ snapshot: e.map.snapshotId })}"
                    >See the world after Episode ${e.number}</a
                  >
                </p>`
              : html`<p class="muted">No map snapshot for this episode.</p>`
          }
        </aside>
      </div>
    `,
  };
}

export function characters(m: ReleaseManifest): Page {
  return {
    title: 'Characters',
    body: html`<h1>Characters</h1>
      <ul class="grid plain">
        ${m.characters.map(characterCard)}
      </ul>`,
  };
}

export function character(m: ReleaseManifest, r: Route): Page {
  const c = m.characters.find((x) => x.slug === r.params.slug);
  if (!c) return notFound();
  const skin = assetUrl(m, c.skinAssetId);
  const lore = m.lore.filter((l) => l.characterIds.includes(c.id));
  return {
    title: c.displayName,
    body: html`
      <p class="kicker"><a href="/characters">Characters</a></p>
      <div class="two-col char-page accent-${c.accent}">
        <ka-skin-viewer
          skin=${skin ?? ''}
          model=${c.modelType}
          label="${c.displayName} in Minecraft"
        >
          <div class="placeholder">
            ${spiderSvg(64)}
            <p>Placeholder — the approved skin hasn't been added yet.</p>
          </div>
        </ka-skin-viewer>
        <div>
          <h1>${c.displayName}</h1>
          <p><span class="tag">${canonLabel[c.canonStatus]}</span></p>
          <ka-markdown .source=${c.biography}></ka-markdown>
          ${
            c.unknowns.length
              ? html`<h2>Unsolved mysteries</h2>
                  <ul>
                    ${c.unknowns.map((u) => html`<li>${u}</li>`)}
                  </ul>`
              : nothing
          }
          ${
            lore.length
              ? html`<h2>Lore</h2>
                  <ul>
                    ${lore.map((l) => html`<li><a href="/lore/${l.slug}">${l.title}</a></li>`)}
                  </ul>`
              : nothing
          }
        </div>
      </div>
    `,
  };
}

export function lore(m: ReleaseManifest, rewardIds: Set<string>, secretPanel: unknown): Page {
  const visible = m.lore.filter((l) => !rewardIds.has(l.id));
  return {
    title: 'Lore',
    body: html`<h1>Lore</h1>
      <ul class="grid plain">
        ${visible.map(
          (l) =>
            html`<li class="panel">
              <h3><a href="/lore/${l.slug}">${l.title}</a></h3>
              <span class="tag">${canonLabel[l.canonStatus]}</span>
            </li>`,
        )}
      </ul>
      ${secretPanel}
      <ka-spider-symbol symbol="lore" class="hidden-spider"></ka-spider-symbol>`,
  };
}

export function loreEntry(m: ReleaseManifest, r: Route): Page {
  const l = m.lore.find((x) => x.slug === r.params.slug);
  if (!l) return notFound();
  const chars = m.characters.filter((c) => l.characterIds.includes(c.id));
  const places = m.landmarks.filter((x) => l.landmarkIds.includes(x.id));
  return {
    title: l.title,
    body: html`<article class="panel scroll">
      <p class="kicker"><a href="/lore">Lore</a></p>
      <h1>${l.title}</h1>
      <p><span class="tag">${canonLabel[l.canonStatus]}</span></p>
      <ka-markdown .source=${l.body}></ka-markdown>
      ${chars.length ? html`<p>Characters: ${chars.map((c, i) => html`${i ? ', ' : ''}<a href="/characters/${c.slug}">${c.displayName}</a>`)}</p>` : nothing}
      ${
        places.length
          ? html`<p>
              Places:
              ${places.map((p, i) => html`${i ? ', ' : ''}<a href="/map?${formatMapLink({ landmark: p.id })}">${p.name}</a> (${DIMENSION_LABELS[p.dimension]})`)}
            </p>`
          : nothing
      }
    </article>`,
  };
}

export function about(m: ReleaseManifest | null): Page {
  return {
    title: 'About',
    body: html`<section class="panel">
      <h1>About</h1>
      <p>
        King Arachnid Chronicles is a family project: a father and son recording their Minecraft
        adventures as King Arachnid and Kynorum.
      </p>
      <p>The map shows saved snapshots of our world after each episode. It is never live.</p>
      <p class="muted">
        An unofficial fan project. Not an official Minecraft product. Not approved by or associated
        with Mojang or Microsoft.
      </p>
      ${m?.publishedAt ? html`<p class="muted">Last updated ${fmtDate(m.publishedAt)}.</p>` : nothing}
    </section>`,
  };
}

export { fmtDate, fmtTime };
